import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { clearAuth } from "../../auth/auth";
import UserAvatar from "../../components/userAvatar/userAvatar";
import { historyIcon, logoutIcon, messageIcon, settingsIcon, userActivityIcon } from "../../utils/config";
import { useI18n } from "../../utils/translate/i18n/useI18n";
import { logActivity } from "../../utils/logActivity";
import { useAuthUser } from "../../utils/useAuthUser";
import RegisterModal from "../../modal/registerModal/registerModal";
import LoginModal from "../../modal/loginModal/loginModal";
import ShareOpinionModal from "../../modal/shareOpinionModal/shareOpinionModal";
import logo from "../../assets/Logo Swiat Sluchu.png";
import "./header.scss";

const TopBar: React.FC = () => {
  const { t, isEnglish, setLocale } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const toggleDropdown = () => setShowDropdown(!showDropdown);
  const handleGoHome = () => nav("/");

  const isPureTone = location.pathname === "/" || location.pathname === "/tonal-audiometry";
  const isVerbal   = location.pathname === "/verbal-audiometry";
  const isAudiometer = location.pathname === "/audiometer";
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showShareOpinion, setShowShareOpinion] = useState(false);

  const user = useAuthUser(); 
  
  const openHistory = () => {
    nav("/user-results");
    setShowDropdown(false);
  };

  const handleLogout = () => {
    clearAuth();
    nav("/");
    setShowDropdown(false);
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);


  return (
    <header className="header navbar sticky-top">
      <div className="container-fluid header__container">

        <div className="d-flex align-items-center gap-3">
          <button
            onClick={handleGoHome}
            className="btn btn-outline-light header__home-btn"
            title={t("Strona główna (start test)")}
            aria-label={t("Strona główna (start test)")}
          >
            <i className="bi bi-house-door-fill"></i>
          </button>

          <div>
            <p className="header__eyebrow mb-1">{t("Audiometria online")}</p>
            <h1 className="header__title mb-0">{t("Test Słuchu")}</h1>
          </div>
          <a href="https://www.swiat-sluchu.pl" target="_blank" rel="noopener noreferrer" aria-label="Świat Słuchu">
            <img src={logo} alt="Świat Słuchu" className="header__logo" />
          </a>
        </div>

        {/* ── Desktop inline tabs (centered) ──────────────────────────── */}
          {user != null && (
            <div className="header__tabs-inline d-none d-lg-flex" role="tablist" aria-label={t("Typ badania")}>
              <button
                role="tab"
                aria-selected={isPureTone}
                className={`header__tab${isPureTone ? " header__tab--active" : ""}`}
                onClick={() => nav("/tonal-audiometry")}
                title={t("Audiometria tonalna")}
              >
                <i className="bi bi-soundwave" aria-hidden="true" />
                <span>{t("Audiometria tonalna")}</span>
              </button>
              <button
                role="tab"
                aria-selected={isVerbal}
                className={`header__tab${isVerbal ? " header__tab--active" : ""}`}
                onClick={() => nav("/verbal-audiometry")}
                title={t("Audiometria słowna")}
              >
                <i className="bi bi-mic-fill" aria-hidden="true" />
                <span>{t("Audiometria słowna")}</span>
              </button>
              <button
                role="tab"
                aria-selected={isAudiometer}
                className={`header__tab${isAudiometer ? " header__tab--active" : ""}`}
                onClick={() => nav("/audiometer")}
                title={t("Audiometer")}
              >
                <i className="bi bi-sliders" aria-hidden="true" />
                <span>{t("Audiometer")}</span>
              </button>
            </div>
          )}
          <div className="header-user-container" ref={dropdownRef}>
            <button
              className="header-right-menu-btn header__lang-btn"
              onClick={() => { setLocale(isEnglish ? "pl-PL" : "en-US"); logActivity("change_language", window.location.pathname, isEnglish ? "Zmieniono język na polski" : "Changed language to English", undefined); }}
              title={isEnglish ? "Zmień język na polski" : "Change language to English"}
              aria-label={isEnglish ? "Zmień język na polski" : "Change language to English"}
            >
              {isEnglish ? "EN" : "PL"}
            </button>

            <button
              className="header-right-menu-btn header__info-btn"
              onClick={() => window.dispatchEvent(new Event("show-info-modal"))}
              title={t("Informacje o badaniu")}
              aria-label={t("Informacje o badaniu")}
            >
              <i className="bi bi-info-circle"></i>
            </button>

            <button className="header-right-menu-btn" onClick={toggleDropdown} aria-label={t("Menu użytkownika")}>
              <UserAvatar avatar={user?.avatar} username={user?.username} />
              <span className={user === null ? "header-guest-label" : ""}>
                {user?.username ?? t("Niezalogowany")}
              </span>
            </button>

            {showDropdown && (
              <div className="header-user-dropdown">
                <div className="header-user-dropdown-title">
                  <UserAvatar avatar={user?.avatar} username={user?.username} />
                  <div className={`header-user-name${user === null ? " header-user-name--guest" : ""}`}>
                    {user?.username ?? t("Niezalogowany")}
                  </div>
                  <div className="header-user-email">{user?.email ?? t("Brak emaila")}</div>
                  {user === null && (
                    <p className="header-user-guest-note">
                      <b>{t("Nie jesteś zalogowany.")}</b><br />
                      {t("Po zalogowaniu zyskasz dostęp do historii swoich wyników, dodatkowych opcj oraz możliwości personalizacji aplikacji. Dzięki temu możliwe jest korzystanie z rozszerzonych funkcji.")}
                    </p>
                  )}
                </div>
                <div className="header-dropdown-divider"></div>
            
                {user !== null && (
                  <>
                    <button className="header-user-dropdown-item" onClick={openHistory}>
                      {historyIcon} {t("Historia badań")}
                    </button>

                    <button className="header-user-dropdown-item" onClick={() => { nav("/user-activity"); setShowDropdown(false); }}>
                      {userActivityIcon} {t("Aktywność użytkowników")}
                    </button>                    

                    <button className="header-user-dropdown-item" onClick={() => { nav("/user-settings"); setShowDropdown(false); }}>
                      {settingsIcon} {t("Ustawienia")}
                    </button>

                    <button className="header-user-dropdown-item" onClick={() => { setShowShareOpinion(true); setShowDropdown(false); }}>
                      {messageIcon} {t("Podziel się opinią")}
                    </button>

                    <button className="header-user-dropdown-item" onClick={handleLogout}>
                      {logoutIcon} {t("Wyloguj się")}
                    </button>    
                  </>             
                  )
                }
                {user === null && (
                  <>
                    <button className="header-user-dropdown-item" onClick={() => { setShowShareOpinion(true); setShowDropdown(false); }}>
                      {messageIcon} {t("Podziel się opinią")}
                    </button>
                    <button className="header-user-dropdown-item" onClick={() => { setShowLogin(true); setShowDropdown(false); }}>
                      {settingsIcon} {t("Zaloguj")}
                    </button>
                  </>
                )}



                {/*             
                <button className="header-user-dropdown-item" onClick={openChangePassword}>
                  {changePasswordIcon} Change password
                </button>
                <button className="header-user-dropdown-item" onClick={openMessage}>
                  {messageIcon} Share your opinion
                </button>                
*/}
              </div>
            )}
          </div>

      </div>

      {user != null && (
        <div className="header__tabs d-lg-none" role="tablist" aria-label={t("Typ badania")}>
          <button
            role="tab"
            aria-selected={isPureTone}
            className={`header__tab${isPureTone ? " header__tab--active" : ""}`}
            onClick={() => nav("/tonal-audiometry")}
            title={t("Audiometria tonalna")}
          >
            <i className="bi bi-soundwave" aria-hidden="true" />
            <span>{t("Audiometria tonalna")}</span>
          </button>
          <button
            role="tab"
            aria-selected={isVerbal}
            className={`header__tab${isVerbal ? " header__tab--active" : ""}`}
            onClick={() => nav("/verbal-audiometry")}
            title={t("Audiometria słowna")}
          >
            <i className="bi bi-mic-fill" aria-hidden="true" />
            <span>{t("Audiometria słowna")}</span>
          </button>
          <button
            role="tab"
            aria-selected={isAudiometer}
            className={`header__tab${isAudiometer ? " header__tab--active" : ""}`}
            onClick={() => nav("/audiometer")}
            title={t("Audiometer")}
          >
            <i className="bi bi-sliders" aria-hidden="true" />
            <span>{t("Audiometer")}</span>
          </button>
        </div>
      )}

      <RegisterModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onOpenLogin={() => {
          setShowRegister(false);
          setShowLogin(true);
        }}
      />
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onOpenRegister={() => {
          setShowLogin(false);
          setShowRegister(true);
        }}
      />
      <ShareOpinionModal
        isOpen={showShareOpinion}
        onClose={() => setShowShareOpinion(false)}
      />
    </header>
  );
};

export default TopBar;